import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, qs } from '../api';
import { Icon } from '../components/icons';
import { Empty, EntryStatusBadge, ErrorBox, Field, Loading, Modal, PageHead } from '../components/ui';
import { addDays, fmtDayShort, fmtDistance, fmtHours, fmtTime, fromLocalInput, mapsLink, startOfWeek, toDateInput, toLocalInput } from '../format';
import { useI18n } from '../i18n';
import type { EntryStatus, TimeEntry, User } from '../types';

export function TimesheetsPage() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const eventId = params.get('eventId') ?? '';
  const [from, setFrom] = useState(() => toDateInput(startOfWeek()));
  const [to, setTo] = useState(() => toDateInput(addDays(startOfWeek(), 6)));
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState<EntryStatus | ''>('');
  const [rejecting, setRejecting] = useState<TimeEntry | null>(null);
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [bulkError, setBulkError] = useState<unknown>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/users') });
  // Dates are local days; the API compares clock-in times.
  const dayStart = (d: string) => (d ? new Date(`${d}T00:00`) : null);
  const fromD = dayStart(from);
  const toD = dayStart(to);
  const range = eventId ? {} : { from: fromD?.toISOString(), to: toD ? addDays(toD, 1).toISOString() : undefined };
  const filters = { ...range, userId, status, eventId };
  const entries = useQuery({ queryKey: ['time-entries', filters], queryFn: () => api.get<TimeEntry[]>(`/time-entries${qs(filters)}`) });
  const rows = entries.data ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['time-entries'] });
    qc.invalidateQueries({ queryKey: ['overview'] });
  };
  const approve = useMutation({ mutationFn: (id: string) => api.post(`/time-entries/${id}/approve`), onSuccess: invalidate });
  const fullyVerified = (r: TimeEntry) => !r.isManual && r.clockInVerified && r.clockOutVerified;
  const bulk = rows.filter((r) => r.status === 'submitted' && fullyVerified(r));
  const approveAllVerified = async () => {
    setBulkError(null);
    try {
      for (const r of bulk) await api.post(`/time-entries/${r.id}/approve`);
    } catch (e) {
      setBulkError(e);
    }
    invalidate();
  };

  const totals = useMemo(() => {
    const m = new Map<string, { name: string; hours: number; approved: number; n: number }>();
    for (const r of rows) {
      const x = m.get(r.userId) ?? { name: r.userName, hours: 0, approved: 0, n: 0 };
      x.hours += r.hours ?? 0;
      if (r.status === 'approved') x.approved += r.hours ?? 0;
      x.n++;
      m.set(r.userId, x);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const shiftWeek = (n: number) => {
    const f = addDays(new Date(`${from}T00:00`), 7 * n);
    setFrom(toDateInput(f));
    setTo(toDateInput(addDays(f, 6)));
  };

  return (
    <>
      <PageHead
        title={t('timesheets.title')}
        sub={t('timesheets.sub')}
        actions={
          <>
            {bulk.length > 0 && (
              <button className="btn btn-good" onClick={approveAllVerified}>
                <Icon.check />
                {t('timesheets.approveVerified', { n: bulk.length })}
              </button>
            )}
            <button className="btn" onClick={() => api.download(`/time-entries/export.xlsx${qs(filters)}`, 'timesheet.xlsx')} disabled={!rows.length}>
              <Icon.download />
              {t('timesheets.export')}
            </button>
          </>
        }
      />
      {eventId ? (
        <div className="alert alert-warn" style={{ marginBottom: 14 }}>
          {t('timesheets.filteredByEvent')} <button className="btn btn-sm" onClick={() => setParams({})}>{t('timesheets.clearFilter')}</button>
        </div>
      ) : (
        <div className="toolbar">
          <button className="btn btn-sm" onClick={() => shiftWeek(-1)} aria-label={t('timesheets.prevWeek')}>←</button>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label={t('timesheets.from')} />
          <span className="muted">–</span>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label={t('timesheets.to')} />
          <button className="btn btn-sm" onClick={() => shiftWeek(1)} aria-label={t('timesheets.nextWeek')}>→</button>
          <select className="select" value={userId} onChange={(e) => setUserId(e.target.value)} aria-label={t('timesheets.technician')}>
            <option value="">{t('timesheets.everyone')}</option>
            {(users.data ?? []).filter((u) => u.role === 'lead' || u.role === 'technician').map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as EntryStatus | '')} aria-label={t('common.status')}>
            <option value="">{t('equipment.allStatuses')}</option>
            {(['open', 'submitted', 'approved', 'rejected'] as EntryStatus[]).map((s) => <option key={s} value={s}>{t(`entryStatus.${s}`)}</option>)}
          </select>
        </div>
      )}
      <ErrorBox error={approve.error ?? bulkError} />

      {totals.length > 0 && (
        <div className="row" style={{ marginBottom: 14 }}>
          {totals.map((x) => (
            <div key={x.name} className="card stat" style={{ minWidth: 150 }}>
              <div className="label">{x.name}</div>
              <div className="value" style={{ fontSize: 20 }}>{fmtHours(x.hours)}</div>
              <div className="hint">{t('timesheets.approvedHours', { h: fmtHours(x.approved) })}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card table-wrap">
        {entries.isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty title={t('timesheets.empty')} text={t('timesheets.emptyText')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('timesheets.technician')}</th>
                <th>{t('events.date')}</th>
                <th>{t('events.event')}</th>
                <th>{t('timesheets.in')}</th>
                <th>{t('timesheets.out')}</th>
                <th className="right">{t('timesheets.break')}</th>
                <th className="right">{t('timesheets.hours')}</th>
                <th>{t('timesheets.locationCheck')}</th>
                <th>{t('common.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.userName}</strong></td>
                  <td className="nowrap">{fmtDayShort(r.clockInAt, lang)}</td>
                  <td><Link to={`/events/${r.eventId}`}>{r.eventTitle}</Link><div className="small muted">{r.venueName}</div></td>
                  <td className="num">{fmtTime(r.clockInAt, lang)}</td>
                  <td className="num">{r.clockOutAt ? fmtTime(r.clockOutAt, lang) : <span className="badge badge-blue">{t('timesheets.onSite')}</span>}</td>
                  <td className="right num">{r.breakMinutes ? `${r.breakMinutes}′` : ''}</td>
                  <td className="right num"><strong>{fmtHours(r.hours)}</strong></td>
                  <td className="small"><LocationCheck r={r} /></td>
                  <td>
                    <EntryStatusBadge status={r.status} />
                    {r.reviewNote && <div className="small muted" title={r.reviewNote}>{r.reviewNote.slice(0, 40)}</div>}
                  </td>
                  <td className="right nowrap">
                    {r.status !== 'open' && (
                      <>
                        {r.status !== 'approved' && (
                          <button className="btn btn-sm btn-good" onClick={() => approve.mutate(r.id)} disabled={approve.isPending}>{t('timesheets.approve')}</button>
                        )}{' '}
                        {r.status !== 'rejected' && <button className="btn btn-sm btn-danger" onClick={() => setRejecting(r)}>{t('timesheets.reject')}</button>}{' '}
                        <button className="btn btn-sm" onClick={() => setEditing(r)}>{t('common.edit')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {rejecting && <RejectModal entry={rejecting} onClose={() => setRejecting(null)} onDone={invalidate} />}
      {editing && <EditEntryModal entry={editing} onClose={() => setEditing(null)} onDone={invalidate} />}
    </>
  );
}

/** Shows whether clock-in and clock-out happened on site, with a map link for each point. */
function LocationCheck({ r }: { r: TimeEntry }) {
  const t = useI18n().t;
  if (r.isManual) {
    return (
      <div>
        <span className="badge badge-amber">{t('timesheets.manual')}</span>
        {r.manualReason && <div className="muted" title={r.manualReason}>{r.manualReason.slice(0, 60)}</div>}
      </div>
    );
  }
  const point = (ok: boolean, dist: number | null, lat: number | null, lng: number | null, label: string) => (
    <div className="nowrap">
      <span className={`badge ${ok ? 'badge-green' : 'badge-red'}`}>{ok ? '✓' : '✗'} {label}</span>{' '}
      {lat !== null && lng !== null ? (
        <a href={mapsLink(lat, lng)} target="_blank" rel="noreferrer">{fmtDistance(dist)}</a>
      ) : (
        <span className="muted">{fmtDistance(dist)}</span>
      )}
    </div>
  );
  return (
    <div className="stack" style={{ gap: 3 }}>
      {point(r.clockInVerified, r.clockInDistanceM, r.clockInLat, r.clockInLng, t('timesheets.in'))}
      {r.clockOutAt && point(r.clockOutVerified, r.clockOutDistanceM, r.clockOutLat, r.clockOutLng, t('timesheets.out'))}
      {r.clockOutReason && <div className="muted" title={r.clockOutReason}>“{r.clockOutReason.slice(0, 60)}”</div>}
    </div>
  );
}

function RejectModal({ entry, onClose, onDone }: { entry: TimeEntry; onClose: () => void; onDone: () => void }) {
  const t = useI18n().t;
  const [note, setNote] = useState('');
  const reject = useMutation({
    mutationFn: () => api.post(`/time-entries/${entry.id}/reject`, { note }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Modal
      title={t('timesheets.rejectTitle', { name: entry.userName })}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-danger" disabled={!note.trim() || reject.isPending} onClick={() => reject.mutate()}>{t('timesheets.reject')}</button>
        </>
      }
    >
      <ErrorBox error={reject.error} />
      <Field label={t('timesheets.rejectReason')} hint={t('timesheets.rejectHint')} htmlFor="rj-note">
        <textarea id="rj-note" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

function EditEntryModal({ entry, onClose, onDone }: { entry: TimeEntry; onClose: () => void; onDone: () => void }) {
  const t = useI18n().t;
  const [f, setF] = useState({ clockInAt: toLocalInput(entry.clockInAt), clockOutAt: toLocalInput(entry.clockOutAt), breakMinutes: String(entry.breakMinutes), reason: '' });
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/time-entries/${entry.id}`, {
        clockInAt: fromLocalInput(f.clockInAt),
        clockOutAt: f.clockOutAt ? fromLocalInput(f.clockOutAt) : undefined,
        breakMinutes: Number(f.breakMinutes) || 0,
        reason: f.reason,
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Modal
      title={t('timesheets.editTitle', { name: entry.userName })}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={f.reason.trim().length < 3 || save.isPending} onClick={() => save.mutate()}>{t('common.save')}</button>
        </>
      }
    >
      <div className="alert alert-warn">{t('timesheets.editWarning')}</div>
      <ErrorBox error={save.error} />
      <div className="form-grid">
        <Field label={t('timesheets.in')} htmlFor="ed-in">
          <input id="ed-in" className="input" type="datetime-local" value={f.clockInAt} onChange={(e) => setF({ ...f, clockInAt: e.target.value })} />
        </Field>
        <Field label={t('timesheets.out')} htmlFor="ed-out">
          <input id="ed-out" className="input" type="datetime-local" value={f.clockOutAt} onChange={(e) => setF({ ...f, clockOutAt: e.target.value })} />
        </Field>
        <Field label={t('timesheets.breakMinutes')} htmlFor="ed-br">
          <input id="ed-br" className="input" type="number" min="0" value={f.breakMinutes} onChange={(e) => setF({ ...f, breakMinutes: e.target.value })} />
        </Field>
        <Field label={t('timesheets.editReason')} full htmlFor="ed-reason">
          <textarea id="ed-reason" className="textarea" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
